import React, { useEffect, useMemo, useState } from "react";
import * as Phosphor from "@phosphor-icons/react";
import { fetchForecast, fetchRegions } from "@/lib/api";
import RegionSelect from "@/components/RegionSelect";

export default function Forecast() {
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("weather"); // weather | alerts | disease | bbch
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const rs = await fetchRegions();
      setRegions(rs);
      if (!regionId && rs[0]) setRegionId(rs[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!regionId) return;
    setLoading(true);
    (async () => {
      try { setData(await fetchForecast(regionId)); }
      finally { setLoading(false); }
    })();
  }, [regionId]);

  const days = data?.forecast?.days || [];
  const adv = data?.forecast?.advisories || {};
  const bbch = data?.bbch || {};

  return (
    <div className="space-y-5" data-testid="forecast-root">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eid-label">Ausblick · 7 Tage</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-1">Wetter &amp; Risiken</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Prognose, Frost-Warnung, Krankheits-Druck und Entwicklungsstadium
            für deine Region — aus Open-Meteo, kostenlos.
          </p>
        </div>
        <RegionSelect regions={regions} value={regionId} onChange={setRegionId} testid="forecast-region-select" />
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="forecast-tabs">
        {TABS.map((t) => {
          const Icon = Phosphor[t.icon] || Phosphor.Info;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`forecast-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors ${
                active ? "bg-[#F5F5F5] text-[#0A0A0A] border-white/15" : "bg-[#121212] border-white/10 hover:bg-white/5"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="eid-card p-6 text-sm text-zinc-400">Lade Prognose…</div>
      ) : days.length === 0 ? (
        <div className="eid-card p-6 text-sm text-zinc-400">
          Keine Prognose verfügbar. Versuch es in einer Minute nochmal — Open-Meteo wendet ab und zu Rate-Limits an.
        </div>
      ) : (
        <>
          {tab === "weather" && <WeatherTab days={days} adv={adv} />}
          {tab === "alerts" && <AlertsTab adv={adv} days={days} />}
          {tab === "disease" && <DiseaseTab adv={adv} crop={data?.crop} />}
          {tab === "bbch" && <BBCHTab bbch={bbch} crop={data?.crop} />}
        </>
      )}
    </div>
  );
}

const TABS = [
  { id: "weather", label: "7-Tage-Wetter", icon: "CloudSun" },
  { id: "alerts", label: "Frost &amp; Bewässerung", icon: "Snowflake" },
  { id: "disease", label: "Schädlings-Druck", icon: "Bug" },
  { id: "bbch", label: "Wachstumsstadium", icon: "Plant" },
];

function WeatherTab({ days, adv }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Chip label="Bewässerung" value={labelSignal(adv.irrigation_signal)} color={colorSignal(adv.irrigation_signal)}
              sub={adv.irrigation_balance_mm_5d != null ? `${adv.irrigation_balance_mm_5d} mm Defizit 5d` : "—"} />
        <Chip label="Frost" value={adv.frost_days?.length ? `${adv.frost_days.length} Tag(e)` : "Keine"}
              color={adv.frost_days?.length ? "#FF5500" : "#7EE8B0"} sub={adv.hard_frost_days?.length ? "Harter Frost" : "Leicht"} />
        <Chip label="Aussaat-Fenster" value={adv.sowing_window_days?.length || 0}
              color={(adv.sowing_window_days?.length || 0) > 2 ? "#00FA9A" : "#FFC24B"} sub="trocken & warmer Boden" />
        <Chip label="Hitzeperiode" value={`${adv.heatwave_run || 0} Tage`}
              color={(adv.heatwave_run || 0) >= 3 ? "#FF5500" : "#8A8A93"} sub="≥ 30 °C am Stück" />
      </div>

      <div className="eid-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="forecast-days-table">
          <thead className="bg-[#0A0A0A]">
            <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              <th className="text-left p-2">Tag</th>
              <th className="text-right p-2">Min/Max</th>
              <th className="text-right p-2">Regen</th>
              <th className="text-right p-2 hidden sm:table-cell">ET₀</th>
              <th className="text-right p-2 hidden md:table-cell">RF %</th>
              <th className="text-right p-2 hidden md:table-cell">Boden °C</th>
              <th className="text-right p-2 hidden lg:table-cell">Wind</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className="border-t border-white/10">
                <td className="p-2 font-mono text-xs">{formatDay(d.date)}</td>
                <td className="p-2 text-right font-mono">
                  <span style={{ color: d.tmin <= 0 ? "#FF5500" : "#F5F5F5" }}>{d.tmin ?? "—"}</span>
                  <span className="text-zinc-500"> / </span>
                  <span style={{ color: d.tmax >= 30 ? "#FF5500" : "#F5F5F5" }}>{d.tmax ?? "—"}</span>
                </td>
                <td className="p-2 text-right font-mono" style={{ color: (d.precip || 0) >= 5 ? "#00FA9A" : "#F5F5F5" }}>
                  {d.precip ?? "—"} mm
                </td>
                <td className="p-2 text-right font-mono hidden sm:table-cell">{d.et0 ?? "—"} mm</td>
                <td className="p-2 text-right font-mono hidden md:table-cell">{d.rh ?? "—"}</td>
                <td className="p-2 text-right font-mono hidden md:table-cell">{d.tsoil ?? "—"}</td>
                <td className="p-2 text-right font-mono hidden lg:table-cell">{d.wind ?? "—"} km/h</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertsTab({ adv }) {
  const frostMsg = adv.frost_days?.length
    ? `Nachtfrost an ${adv.frost_days.length} Tag(en): ${adv.frost_days.join(", ")}. Empfindliche Kulturen (Raps-Blüte, Obstknospen, Jungpflanzen) schützen.`
    : "Keine Frostgefahr in den nächsten 7 Tagen.";
  const hardFrostMsg = adv.hard_frost_days?.length
    ? `Harter Frost (<-3 °C) an: ${adv.hard_frost_days.join(", ")}. Frostberegnung oder Abdeckung.`
    : null;
  const irrSignalLabel = {
    urgent: "DRINGEND — jetzt bewässern, wenn möglich",
    plan: "Bewässerung in 3–5 Tagen einplanen",
    ok: "Wasserhaushalt in Ordnung",
  }[adv.irrigation_signal] || "—";

  return (
    <div className="space-y-3">
      <AlertCard icon="Snowflake" title="Frost in 7 Tagen" body={frostMsg} tone={adv.frost_days?.length ? "critical" : "good"} />
      {hardFrostMsg && (
        <AlertCard icon="Snowflake" title="Extrem-Frost" body={hardFrostMsg} tone="critical" />
      )}
      <AlertCard icon="Drop" title="Bewässerungs-Ampel" tone={toneIrr(adv.irrigation_signal)}
                 body={`${irrSignalLabel}. Kumulierte ET₀-Niederschlag-Bilanz der nächsten 5 Tage: ${adv.irrigation_balance_mm_5d ?? 0} mm.`} />
      <AlertCard icon="Sun" title="Hitzeperiode" tone={(adv.heatwave_run || 0) >= 3 ? "critical" : "ok"}
                 body={(adv.heatwave_run || 0) >= 3
                    ? `${adv.heatwave_run} Tage über 30 °C. Bestände können Hitzestress entwickeln — Bewässerung morgens/abends, nicht mittags.`
                    : "Keine länger anhaltende Hitze erwartet."} />
    </div>
  );
}

function DiseaseTab({ adv, crop }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <AlertCard icon="Bug" title="Krautfäule (Phytophthora)"
                 tone={toneDisease(adv.blight_signal)}
                 body={`Risiko: ${capitalize(adv.blight_signal || "low")}. ${adv.blight_risk_days || 0} Tag(e) mit feucht-warmen Bedingungen (RF ≥ 85 %, 10–30 °C).
${crop === "corn" || crop === "wheat" ? "Für Kartoffel-Bestände prüfen; Getreide i.d.R. weniger betroffen." : "Besonders relevant bei Kartoffeln."}`} />
      <AlertCard icon="Leaf" title="Mehltau (Erysiphe)"
                 tone={toneDisease(adv.mildew_signal)}
                 body={`Risiko: ${capitalize(adv.mildew_signal || "low")}. ${adv.mildew_risk_days || 0} Tag(e) mit warmen Nächten + mittlerer Luftfeuchte. Fungizid-Schwellenwerte im Blick behalten.`} />
      <AlertCard icon="Bug" title="Blattlaus-Druck"
                 tone={toneDisease(adv.aphid_signal)}
                 body={`Risiko: ${capitalize(adv.aphid_signal || "low")}. ${adv.aphid_risk_days || 0} Tag(e) warm (≥ 20 °C), windarm, trocken. Beobachte Fahnen-Blätter auf Läuse.`} />
    </div>
  );
}

function BBCHTab({ bbch, crop }) {
  return (
    <div className="eid-card p-5 space-y-3">
      <div className="flex items-center gap-3">
        <span className="p-2 rounded-sm bg-[#00FA9A]/10 text-[#00FA9A]">
          <Phosphor.Plant size={22} />
        </span>
        <div>
          <div className="eid-label">{capitalize(cropLabel(crop))}</div>
          <h2 className="text-2xl font-medium tracking-tight">{bbch.label || "Unbekannt"}</h2>
          <div className="text-xs text-zinc-500 font-mono">
            BBCH {bbch.bbch || "?"} · GDD kumulativ: {bbch.gdd_cum != null ? `${bbch.gdd_cum} °C·d` : "—"}
          </div>
        </div>
      </div>
      {bbch.tip && (
        <div className="p-3 rounded-sm bg-[#0A0A0A] border-l-2 border-[#00FA9A]/40 text-sm">
          <div className="eid-label" style={{ color: "#00FA9A" }}>Was jetzt zu tun ist</div>
          <div className="mt-1">{bbch.tip}</div>
        </div>
      )}
      <p className="text-xs text-zinc-500 leading-relaxed">
        Die BBCH-Skala (00–99) ist der landwirtschaftliche Standard für Entwicklungsstadien.
        Unsere Schätzung basiert auf der kumulierten Wärmesumme (Basis 10 °C). Sie dient als
        Orientierung — für präzise Maßnahmen bleibt die Feldbegehung unverzichtbar.
      </p>
    </div>
  );
}

function AlertCard({ icon, title, body, tone }) {
  const Icon = Phosphor[icon] || Phosphor.Info;
  const colors = {
    good: { bg: "rgba(0,250,154,0.12)", fg: "#00FA9A", stripe: "#00FA9A" },
    ok: { bg: "#131513", fg: "#3F5A2B", stripe: "#7EE8B0" },
    warn: { bg: "#17130C", fg: "#9E6316", stripe: "#FFC24B" },
    critical: { bg: "rgba(255,85,0,0.10)", fg: "#FF7A45", stripe: "#FF5500" },
  }[tone || "ok"];
  return (
    <div className="eid-card p-4" style={{ borderLeft: `3px solid ${colors.stripe}` }}>
      <div className="flex items-start gap-3">
        <span className="p-2 rounded-sm" style={{ background: colors.bg, color: colors.fg }}>
          <Icon size={18} />
        </span>
        <div className="flex-1">
          <div className="text-sm font-medium tracking-tight text-white">{title}</div>
          <div className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line mt-1">{body}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, sub, color }) {
  return (
    <div className="eid-card p-3">
      <div className="eid-label">{label}</div>
      <div className="font-mono text-xl mt-0.5" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function formatDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
}
function labelSignal(s) {
  return ({ urgent: "Dringend", plan: "Planen", ok: "OK" }[s] || "—");
}
function colorSignal(s) {
  return ({ urgent: "#FF5500", plan: "#FFC24B", ok: "#00FA9A" }[s] || "#8A8A93");
}
function toneIrr(s) { return ({ urgent: "critical", plan: "warn", ok: "good" }[s] || "ok"); }
function toneDisease(s) { return ({ high: "critical", moderate: "warn", low: "good" }[s] || "ok"); }
function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
function cropLabel(c) {
  return ({ wheat: "Weizen", corn: "Mais", forest_lake: "Wald/See" }[c] || c || "—");
}
